import styles from './SelectableTreeVirtualized.module.scss';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Checkbox, Typography } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { TreeNode } from '../types';

// TODO: WW-7568 This can replace SelectableTree - we need to provide ticket to do so.
interface SingleSelectSelectableTreeVirtualizedProps {
  /**
   * If `true`, multiple nodes can be selected and a checkbox will be added to
   * leaf items. If `selectable` is `'all'`, a checkbox will also be added to
   * group items, that can be used to select/deselect the entire group.
   *
   * @default false
   */
  multiple?: false;

  /**
   *
   * Determines whether or not group nodes can be selected.
   *
   * In single select mode, if `selectable` is `'all'`, a group node can be
   * selected by clicking on it. Otherwise, clicking a group node will
   * expand/collapse the group.
   *
   * In multi select mode nodes can only be selected by clicking the checkbox.
   * Clicking a group node will expand/collapse the group. If `selectable` is
   * `'all'`, a checkbox will be rendered in the group node that can be used to
   * select/deselect the entire group.
   *
   * @default 'all'
   */
  selectable?: 'all' | 'leaf';

  /**
   * A list of nodes to display in the tree. A node can be a leaf node or a
   * group node. A node is considered a leaf node if its `children` is
   * either `null` or `undefined`).
   *
   * When a group node is selected, all its descendants are automatically
   * selected. Group checkbox states (checked/indeterminate) are derived
   * from their children's selection state.
   */
  data: TreeNode[];

  /**
   * Id of the selected node.
   */
  selected: string;

  /**
   * Callback called when tree nodes are selected/deselected.
   *
   * @param nodeId Id of the selected node.
   */
  onChange?: (nodeId: string) => void;
  onNodeToggle?: (event: React.SyntheticEvent, nodeIds: string[]) => void;
  expanded?: string[];

  /**
   * Optional callback for lazy loading data as user scrolls.
   * Called with the index of visible items that need to be loaded.
   *
   * @param index Index of the node in the visible nodes array
   */
  fetchMore?: (index: number) => void;

  /**
   * Optional total count of items. If provided, the virtualizer will use this
   * as the total count instead of the current visible nodes length.
   * This enables proper scrollbar sizing and placeholder rendering for lazy loading.
   *
   * When total is provided and exceeds visible nodes, skeleton placeholders
   * will be shown for items beyond the current data if loading is true.
   */
  total?: number;

  /**
   * Optional loading state. When true and total exceeds current data length,
   * skeleton loading placeholders will be shown for items beyond current data.
   * When false or undefined, empty slots are rendered as blank.
   *
   * @default false
   */
  loading?: boolean;
}

interface MultiSelectSelectableTreeVirtualizedProps {
  /**
   * If `true`, multiple nodes can be selected and a checkbox will be added to
   * leaf items. If `selectable` is `'all'`, a checkbox will also be added to
   * group items, that can be used to select/deselect the entire group.
   *
   * @default false
   */
  multiple: boolean;

  /**
   *
   * Determines whether or not group nodes can be selected.
   *
   * In single select mode, if `selectable` is `'all'`, a group node can be
   * selected by clicking on it. Otherwise, clicking a group node will
   * expand/collapse the group.
   *
   * In multi select mode nodes can only be selected by clicking the checkbox.
   * Clicking a group node will expand/collapse the group. If `selectable` is
   * `'all'`, a checkbox will be rendered in the group node that can be used to
   * select/deselect the entire group.
   *
   * @default 'all'
   */
  selectable?: 'all' | 'leaf';

  /**
   * A list of nodes to display in the tree. A node can be a leaf node or a
   * group node. A node is considered a leaf node if its `children` is
   * either `null` or `undefined`).
   *
   * When a group node is selected, all its descendants are automatically
   * selected. Group checkbox states (checked/indeterminate) are derived
   * from their children's selection state.
   */
  data: TreeNode[];

  /**
   * Ids of the selected node.
   */
  selected: string[];

  /**
   * Callback called when tree nodes are selected/deselected.
   *
   * @param nodeId Ids of the selected node.
   */
  onChange?: (nodeIds: string[]) => void;
  onNodeToggle?: (event: React.SyntheticEvent, nodeIds: string[]) => void;
  expanded?: string[];

  /**
   * Optional callback for lazy loading data as user scrolls.
   * Called with the index of visible items that need to be loaded.
   *
   * @param index Index of the node in the visible nodes array
   */
  fetchMore?: (index: number) => void;

  /**
   * Optional total count of items. If provided, the virtualizer will use this
   * as the total count instead of the current visible nodes length.
   * This enables proper scrollbar sizing and placeholder rendering for lazy loading.
   *
   * When total is provided and exceeds visible nodes, skeleton placeholders
   * will be shown for items beyond the current data if loading is true.
   */
  total?: number;

  /**
   * Optional loading state. When true and total exceeds current data length,
   * skeleton loading placeholders will be shown for items beyond current data.
   * When false or undefined, empty slots are rendered as blank.
   *
   * @default false
   */
  loading?: boolean;
}

type SelectableTreeVirtualizedProps =
  | SingleSelectSelectableTreeVirtualizedProps
  | MultiSelectSelectableTreeVirtualizedProps;

interface NodeSelectionState {
  checked: boolean;
  indeterminate: boolean;
}

interface GetNodeSelectionState {
  (nodeId: string): NodeSelectionState;
}

/**
 * Helper function to get all descendant node IDs (including groups) from a given node
 */
const getAllDescendantIds = (
  node: TreeNode,
  nodeById: Map<string, TreeNode>
): string[] => {
  if (!node.children || node.children.length === 0) {
    return [node.id];
  }

  const childIds = node.children.flatMap((child) => {
    const childNode = nodeById.get(child.id);
    return childNode ? getAllDescendantIds(childNode, nodeById) : [];
  });

  return [node.id, ...childIds];
};

/**
 * This picker is supposed to get a treelike dataset and can then single or multiselect nodes and or parents
 * Uses virtualization for performance with large datasets.
 *
 * Now properly manages selection of leaf/group nodes internally:
 * - When a parent node is selected, all its descendants are automatically selected
 * - When a parent node is deselected, all its descendants are automatically deselected
 * - Parent checkbox states (checked/indeterminate) are derived from children
 *
 * Performance optimizations:
 * - O(1) parent lookups via parent map instead of O(n) searches
 * - Only visible nodes are rendered in DOM (virtualization)
 * - Efficient visible node calculation using Set for expanded state
 * - Cached selection state calculations with memoization
 * - Handles 10k+ items smoothly with proper data structure design
 *
 * Lazy loading support:
 * - Optional `total` prop for known total count - enables proper scrollbar sizing
 * - Optional `fetchMore` callback triggered when scrolling near the end
 * - Optional `loading` prop to show skeleton placeholders for items beyond current data
 *
 * @param props
 * @returns
 */
export const SelectableTreeVirtualized = (
  props: SelectableTreeVirtualizedProps
) => {
  const {
    multiple = false,
    selectable = 'all',
    data,
    selected,
    onChange,
    onNodeToggle,
    expanded: externalExpanded,
    fetchMore,
    total,
    loading = false,
  } = props;

  // Internal expanded state when onNodeToggle is not provided
  const [internalExpanded, setInternalExpanded] = useState<string[]>([]);

  // Use external expanded state if provided, otherwise use internal state
  const expanded = externalExpanded ?? internalExpanded;

  // Build efficient lookup maps for O(1) access
  const { nodeById, parentMap, flattenedNodes } = useMemo(() => {
    const nodeById = new Map<string, TreeNode>();
    const parentMap = new Map<string, string>(); // child id -> parent id
    const flattenedNodes: Array<TreeNode & { depth: number }> = [];

    const processNode = (
      node: TreeNode,
      depth = 0,
      parentId: string | null = null
    ): void => {
      // Add to maps
      nodeById.set(node.id, node);
      if (parentId !== null) {
        parentMap.set(node.id, parentId);
      }

      // Add to flattened array with depth
      flattenedNodes.push({ ...node, depth });

      // Process children recursively
      if (node.children) {
        for (const child of node.children) {
          processNode(child, depth + 1, node.id);
        }
      }
    };

    // Process all root nodes
    for (const node of data) {
      processNode(node);
    }

    return { nodeById, parentMap, flattenedNodes };
  }, [data]);

  const getNodeSelectionState = useMemo<GetNodeSelectionState>(() => {
    const selectedSet = new Set(
      Array.isArray(selected) ? selected : [selected]
    );
    const cache = new Map<string, NodeSelectionState>();

    function getNodeSelectionState(nodeId: string): NodeSelectionState {
      // Return cached result if available
      const cachedResult = cache.get(nodeId);
      if (cachedResult) {
        return cachedResult;
      }

      const node = nodeById.get(nodeId);

      if (!node) {
        // This should never happen, unless there is an error somewhere in
        // CustomContent component.
        throw new Error(`Node with id ${nodeId} doesn't exist`);
      }

      // If a group node's id present in the provided selected array, just
      // render it as selected.
      const isSelected = selectedSet.has(nodeId);

      if (isSelected) {
        const result: NodeSelectionState = {
          checked: true,
          indeterminate: false,
        };

        cache.set(nodeId, result);
        return result;
      }

      // If it's a normal leaf node and it's not selected, render it as
      // unchecked.
      if (!node.children) {
        const result: NodeSelectionState = {
          checked: false,
          indeterminate: false,
        };

        cache.set(nodeId, result);
        return result;
      }

      // Derive selection state for empty category classes.
      if (!node.children.length) {
        const result: NodeSelectionState = {
          checked: selectedSet.has(node.id),
          indeterminate: false,
        };

        cache.set(nodeId, result);
        return result;
      }

      // This is a group node and it has not been explicitly selected.
      // Derive its state from the children.
      const childrenStates = node.children.map((child) => {
        return getNodeSelectionState(child.id);
      });

      const checked = childrenStates.every((state) => state.checked);
      const indeterminate =
        !checked &&
        childrenStates.some((state) => state.checked || state.indeterminate);

      const result: NodeSelectionState = {
        checked,
        indeterminate,
      };

      cache.set(nodeId, result);
      return result;
    }

    return getNodeSelectionState;
  }, [selected, nodeById]);

  const isFlat = data.every((x) => !x.children);

  // Filter visible nodes based on expanded state
  const visibleNodes = useMemo(() => {
    if (!expanded || expanded.length === 0) {
      // If nothing is expanded, only show root nodes
      return flattenedNodes.filter((node) => node.depth === 0);
    }

    const expandedSet = new Set(expanded);
    const visibleNodes: Array<TreeNode & { depth: number }> = [];

    // Use parent map for performance
    for (const node of flattenedNodes) {
      if (node.depth === 0) {
        // Always show root nodes
        visibleNodes.push(node);
      } else {
        // Show child nodes only if their parent is expanded
        const parentId = parentMap.get(node.id);
        if (parentId && expandedSet.has(parentId)) {
          visibleNodes.push(node);
        }
      }
    }

    return visibleNodes;
  }, [flattenedNodes, expanded, parentMap]);

  // Virtualization setup - only renders visible DOM elements
  const parentRef = useRef<HTMLDivElement>(null);

  // Use total if provided, otherwise use current visible nodes length
  const virtualizerCount = total !== undefined ? total : visibleNodes.length;

  const virtualizer = useVirtualizer({
    count: virtualizerCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 45,
    enabled: true,
    overscan: 10,
  });
  const items = virtualizer.getVirtualItems();

  // Lazy loading: Call fetchMore when user scrolls near the end
  useEffect(() => {
    if (!fetchMore) return;
    if (items.length === 0) return;

    // Get the last visible item index
    const lastVisibleItem = items[items.length - 1];
    if (!lastVisibleItem) return;

    const lastVisibleIndex = lastVisibleItem.index;
    const currentDataCount = visibleNodes.length;
    const totalCount = total !== undefined ? total : currentDataCount;

    // Trigger fetchMore when we're within 20 items of the end of current data
    // This gives time to load more data before user reaches the actual end
    const threshold = 20;

    // Only fetch if we're near the end of loaded data AND there's more data to load
    if (
      currentDataCount > 0 &&
      lastVisibleIndex >= currentDataCount - threshold &&
      currentDataCount < totalCount
    ) {
      // For lazy loading, we want to pass an index that makes sense for the data structure
      // In a wide tree (many top-level items), this would be the approximate root node index
      // In a deep tree, this would be the visible node index
      // The consumer can use this to determine when to load more root nodes
      fetchMore(lastVisibleIndex);
    }
  }, [fetchMore, items, visibleNodes.length, total]);

  const renderVirtualizedTreeItem = (node: TreeNode & { depth: number }) => {
    const { id, label, children } = node;
    const isLeaf = children == null;
    const canExpand = !isLeaf && children && children.length > 0;
    const isExpanded = expanded?.includes(id) ?? false;
    const isSelected = Array.isArray(selected)
      ? selected.includes(id)
      : selected === id;

    const showCheckbox = multiple && (isLeaf || selectable === 'all');
    const { checked, indeterminate } = getNodeSelectionState(id);
    const canClickSelectNode = isLeaf || selectable === 'all';

    const handleExpansionClick = () => {
      if (canExpand) {
        const newExpanded = isExpanded
          ? expanded.filter((expandedId) => expandedId !== id)
          : [...expanded, id];

        if (onNodeToggle) {
          // If onNodeToggle is provided, call it (external state management)
          onNodeToggle({} as React.SyntheticEvent, newExpanded);
        } else {
          // If onNodeToggle is not provided, manage state internally
          setInternalExpanded(newExpanded);
        }
      }
    };

    const handleSelectionClick = () => {
      if (canClickSelectNode && onChange) {
        if (multiple) {
          const currentSelected = Array.isArray(selected) ? selected : [];
          const currentNode = nodeById.get(id);

          if (!currentNode) return;

          // Determine if we're selecting or deselecting based on checkbox state
          // (not just if the ID is in the array, since parent state is derived from children)
          const shouldDeselect = checked || indeterminate;

          let newSelected: string[];

          if (shouldDeselect) {
            // Deselecting: Remove this node and all its descendants
            const descendantIds = getAllDescendantIds(currentNode, nodeById);
            newSelected = currentSelected.filter(
              (selectedId) => !descendantIds.includes(selectedId)
            );
          } else {
            // Selecting: Add this node and all its descendants
            const descendantIds = getAllDescendantIds(currentNode, nodeById);
            const uniqueIds = new Set([...currentSelected, ...descendantIds]);
            newSelected = Array.from(uniqueIds);
          }

          (onChange as (nodeIds: string[]) => void)(newSelected);
        } else {
          (onChange as (nodeId: string) => void)(id);
        }
      }
    };

    return (
      <div
        key={id}
        className={`${styles.listItemContent} ${
          isSelected && !multiple ? styles.selected : ''
        }`}
        style={{ paddingLeft: `${node.depth * 2.5}rem` }}
      >
        {canExpand && (
          <div className={styles.iconContainer} onClick={handleExpansionClick}>
            {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </div>
        )}

        {!canExpand && !isFlat && (
          <div
            className={`${styles.iconContainer} ${styles['iconContainer--hidden']}`}
          />
        )}

        {showCheckbox && (
          <Checkbox
            sx={{ padding: 1, marginY: -1 }}
            checked={checked}
            indeterminate={indeterminate}
            onClick={(e) => {
              e.stopPropagation();
              handleSelectionClick();
            }}
          />
        )}

        <Typography
          className={styles.label}
          component="div"
          noWrap
          title={typeof label === 'string' ? label : undefined}
          onClick={
            canClickSelectNode ? handleSelectionClick : handleExpansionClick
          }
          sx={{ cursor: 'pointer', flex: 1 }}
        >
          {label}
        </Typography>
      </div>
    );
  };

  return (
    <div ref={parentRef} className={styles.treeContainer}>
      <div
        className={styles.virtualContainer}
        style={{
          height: virtualizer.getTotalSize(),
        }}
      >
        <div
          className={styles.virtualItemsWrapper}
          style={{
            transform: `translateY(${items[0]?.start ?? 0}px)`,
          }}
        >
          {items.map((virtualRow) => (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              className={`${styles.listItem} ${
                virtualRow.index % 2 ? styles.odd : styles.even
              }`}
            >
              {visibleNodes[virtualRow.index] ? (
                renderVirtualizedTreeItem(visibleNodes[virtualRow.index])
              ) : loading ? (
                <div className={styles.skeletonRow}>
                  <div className={styles.skeletonIcon} />
                  <div className={styles.skeletonText} />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
